import { ReactNode } from 'react'
import { Close, SvgIconComponent } from '@mui/icons-material'
import {
  Chip,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material'
import { showSuccessSnackbar } from '@/utils/snackbars'
import copyToClipboard from '../document-actions-menu/copy-to-clipboard'
import { formatValue, getMaxDecimals } from './shared'

type DataGroupNodeProps = {
  title: string
  icon: SvgIconComponent
  children: ReactNode
  amountTotal: string | null
  poNumber?: string | null
  headerColor?: string
  onClose?: () => void
}

export default function DataGroupNode({
  title,
  icon: Icon,
  children,
  amountTotal,
  poNumber,
  headerColor = 'primary.main',
  onClose,
}: DataGroupNodeProps) {
  function handleCopyPoNumber() {
    if (!poNumber) return
    copyToClipboard(poNumber)
    showSuccessSnackbar('PO number copied to clipboard')
  }

  const amountTotalDecimals = amountTotal
    ? Math.max(2, getMaxDecimals([amountTotal]))
    : 0

  return (
    <Card
      elevation={4}
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderRadius: 3,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: headerColor,
          transition: 'background-color 0.2s',
          color: 'primary.contrastText',
          px: 3,
          ...(onClose && { pr: 1.5 }),
          py: 1,
          // Fixed height so close button doesn't make PO card taller than invoice card
          minHeight: 46,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Icon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {poNumber && (
            <Tooltip title="Click to copy PO number">
              <Chip
                label={
                  <>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        opacity: 0.6,
                        mr: 0.75,
                      }}
                    >
                      PO#
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {poNumber}
                    </Typography>
                  </>
                }
                onClick={handleCopyPoNumber}
                size="small"
                sx={{
                  background: 'transparent',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                }}
              />
            </Tooltip>
          )}
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'primary.contrastText',
                // This makes the hover more visible on the colored background
                '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <CardContent sx={{ p: 2 }}>
        {children}
        <Divider />
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ fontFamily: 'monospace' }}
          >
            {formatValue(amountTotal, {
              decimals: amountTotalDecimals,
              prefix: '$',
            })}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}
